import type { ReactNode } from "react";

import { Alert, AlertDescription, AlertTitle, Button } from "@sirdab/ui";

import { useMessages } from "../i18n";

export interface ErrorStateProps {
  /** Already-localised. Prefer the server's `reasonText`; fall back to a catalogue key. */
  message: string;
  title?: string | undefined;
  onRetry?: (() => void) | undefined;
  /** Already-localised retry label. Defaults to `action.retry`. */
  retryLabel?: string | undefined;
  children?: ReactNode;
}

/**
 * The visible end of every failure path. Built alongside the components, not swept up
 * afterwards: an unhandled failure mid-flow is the exact outcome this product exists to
 * prevent (apps/portal/AGENTS.md).
 *
 * It renders a message it was GIVEN. It never authors an explanation for a policy decision.
 */
export function ErrorState({
  message,
  title,
  onRetry,
  retryLabel,
  children,
}: ErrorStateProps): React.JSX.Element {
  const { t } = useMessages();
  return (
    <Alert variant="destructive">
      {title !== undefined ? <AlertTitle>{title}</AlertTitle> : null}
      <AlertDescription>
        <p>{message}</p>
        {children}
      </AlertDescription>
      {onRetry !== undefined ? (
        <Button
          type="button"
          variant="secondary"
          size="xl"
          onClick={onRetry}
          className="mt-2 self-start"
        >
          {retryLabel ?? t("action.retry")}
        </Button>
      ) : null}
    </Alert>
  );
}
